import { useCallback, useRef, useState } from "react";

/**
 * Hook for saving state into a storage (localstorage or sessionstorage)
 * @param key - storage key
 * @param initialValue - initial value
 * @param session - if true, sessionstorage is used (default is localstorage)
 * @returns {[storedValue, setValue]} - value from storage for a given key (if exists, otherwise initialValue), function that sets the value in storage
 */
export default function useStorage(
  key,
  initialValue = "" || {},
  session = false
) {
  const storage = useRef(session ? sessionStorage : localStorage);
  const storageKey = useRef(`tcbase_${key}`);
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = storage.current.getItem(storageKey.current);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    value => {
      try {
        // Allow value to be a function so we have same API as useState
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        storage.current.setItem(
          storageKey.current,
          JSON.stringify(valueToStore)
        );
      } catch (error) {
        console.log(error);
      }
    },
    [storedValue]
  );

  return [storedValue, setValue];
}
